import { AirspaceFeatureType, OpenAirAltitude } from "@nova/constants"
import { useRestrictions } from "features/map/layers/airspace/layers/restriction/restrictionCache"
import { useNotams } from "features/map/layers/airspace/layers/notam/notamCache"
import { useTfrs } from "features/map/layers/airspace/layers/tfr/tfrCache"
import { AirspaceFeatureRef } from "features/map/layers/airspace/types"
import { useAppDispatch } from "integrations/redux/hooks"
import { useMemo } from "react"
import { setActivePopup, setHoveredAirspaces } from "../slice"
import { formatAltitude } from "./AirspaceFeaturePopup"
import classNames from "classnames"

function altitudeToFeet(altitude?: OpenAirAltitude) {
    if (!altitude) return -Infinity
    switch (altitude.reference) {
        case "SFC":
            return 0
        case "UNLIMITED":
            return Infinity
        case "FL":
            return altitude.flightLevel * 100
        case "MSL":
        case "AGL":
            return altitude.feet
    }
}

const typeSortOrder = {
    tfr: 0,
    notam: 1,
    restriction: 2,
} as const

export const AirspaceFeatureListPopup = ({
    features,
    position,
}: {
    features: AirspaceFeatureRef[]
    position: { lat: number; lng: number }
}) => {
    const dispatch = useAppDispatch()
    const {
        restriction: restrictionIds,
        tfr: tfrIds,
        notam: notamIds,
    } = useMemo(
        () =>
            features.reduce(
                (acc, cur) => {
                    acc[cur.type].push(cur.id)
                    return acc
                },
                { restriction: [], tfr: [], notam: [] } as Record<AirspaceFeatureType, string[]>
            ),
        [features]
    )
    const restrictions = useRestrictions(restrictionIds)
    const notams = useNotams(notamIds)
    const tfrs = useTfrs(tfrIds)
    const airspaces = useMemo(() => {
        return [...restrictions, ...notams, ...tfrs].sort((a, b) => {
            const hasAtLeastOneAltitude =
                a.properties.altitudeLower !== undefined || b.properties.altitudeLower !== undefined
            const altitudeDiff =
                altitudeToFeet(b.properties.altitudeLower) -
                altitudeToFeet(a.properties.altitudeLower)
            if (hasAtLeastOneAltitude && altitudeDiff !== 0) return altitudeDiff
            return typeSortOrder[a.type] - typeSortOrder[b.type]
        })
    }, [restrictions, tfrs, notams])
    return (
        <div className="pointer-events-auto w-[300px] rounded-lg bg-black-800 p-2 flex flex-col gap-0.5 backdrop-blur-sm">
            <div className="px-2 py-1 flex justify-between">
                <small className="xs text-white-400">Airspace</small>
                <small className="xs text-white-400">{airspaces.length}</small>
            </div>
            {airspaces.map((airspace) => {
                return (
                    <div
                        key={airspace.properties.id}
                        className="flex flex-col px-2 py-1.5 hover:bg-white-100 group cursor-pointer rounded-lg min-h-[36px] justify-center"
                        onClick={(e) => {
                            e.stopPropagation()
                            dispatch(
                                setActivePopup({
                                    type: "airspace",
                                    source: airspace.type,
                                    id: airspace.properties.id,
                                    placementType: "point",
                                    point: position,
                                    renderedDimensions: { width: 1, height: 1 },
                                })
                            )
                        }}
                        onMouseEnter={() => {
                            dispatch(
                                setHoveredAirspaces([
                                    {
                                        type: airspace.type,
                                        id: airspace.properties.id,
                                    },
                                ])
                            )
                        }}
                        onMouseLeave={() => {
                            dispatch(setHoveredAirspaces([]))
                        }}
                    >
                        <div className="flex gap-1 items-center">
                            <div className="grow shrink min-w-0 flex items-center gap-2">
                                <div
                                    className={classNames("w-1.5 h-1.5 shrink-0 rounded-full", {
                                        "bg-yellow-500": airspace.type === "notam",
                                        "bg-red-500": airspace.type === "tfr",
                                        "bg-gray-100": airspace.type === "restriction",
                                    })}
                                />
                                <div className="grow shrink min-w-0 truncate text-sm text-gray-50 group-hover:text-white">
                                    {airspace.properties.title}
                                </div>
                            </div>
                            <small className="text-gray-200 uppercase text-xs">
                                {airspace.properties.featureType === "notam"
                                    ? airspace.properties.notamType
                                    : airspace.properties.featureType === "tfr"
                                      ? "TFR"
                                      : ""}
                            </small>
                        </div>
                        {(airspace.properties.altitudeLower !== undefined ||
                            airspace.properties.altitudeUpper !== undefined) && (
                            <small className="text-gray-400 font-mono ml-[14px]">
                                {formatAltitude(airspace.properties.altitudeLower)} -{" "}
                                {formatAltitude(airspace.properties.altitudeUpper)}
                            </small>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
